import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { clearLoginCooldown, getLoginCooldown, LOGIN_COOLDOWN_MS, MAX_FAILED_ATTEMPTS, recordFailedLogin } from './loginCooldown';
import './LoginPage.css';

function browserStorage() {
  try { return window.localStorage; } catch { return null; }
}

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(() => getLoginCooldown(browserStorage()));
  const [now, setNow] = useState(Date.now());
  const submitting = useRef(false);
  const { login } = useAuth();

  useEffect(() => {
    const refresh = () => {
      setNow(Date.now());
      const next = getLoginCooldown(browserStorage());
      setCooldown(next);
      if (!next.blockedUntil) {
        setError((previous) => previous.includes('paused in this browser') ? '' : previous);
      }
    };
    const interval = window.setInterval(refresh, 1000);
    window.addEventListener('storage', refresh);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  const secondsRemaining = Math.max(0, Math.ceil((cooldown.blockedUntil - now) / 1000));

  async function handleLogin(e) {
    e.preventDefault();
    if (submitting.current) return;
    const currentCooldown = getLoginCooldown(browserStorage());
    setCooldown(currentCooldown);
    if (currentCooldown.blockedUntil) return;

    submitting.current = true;
    try {
      setError('');
      setLoading(true);
      await login(email.trim(), password);
      clearLoginCooldown(browserStorage());
      // AppRoutes redirects once Firebase has loaded the authenticated user's permissions.
    } catch (err) {
      if (err?.code === 'auth/too-many-requests') {
        setError('Too many sign-in attempts. Please try again later.');
      } else if (['auth/invalid-credential', 'auth/wrong-password', 'auth/user-not-found', 'auth/invalid-email'].includes(err?.code)) {
        const next = recordFailedLogin(browserStorage());
        setCooldown(next);
        setNow(Date.now());
        setError(next.blockedUntil
          ? ''
          : `Failed to sign in. Check your email and password. ${MAX_FAILED_ATTEMPTS - next.attempts} attempt(s) left before a ${LOGIN_COOLDOWN_MS / 60000}-minute pause.`);
      } else {
        setError('Failed to sign in. Please try again.');
      }
      setLoading(false);
    } finally {
      submitting.current = false;
    }
  }

  return (
    <div className="login-container">
      <div className="login-form">
        <h2>Workspace</h2>
        {error && <div className="login-error">{error}</div>}
        {secondsRemaining > 0 && (
          <div className="login-error" role="status">
            Too many incorrect attempts. Try again in {Math.ceil(secondsRemaining / 60)} minute(s).
          </div>
        )}
        <form onSubmit={handleLogin}>
          <div className="form-group">
            <label>Email</label>
            <input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Password</label>
            <input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>
          <div className="form-actions">
            <button type="submit" disabled={loading || secondsRemaining > 0}>
              {loading ? 'Signing in...' : 'Log In'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
